/**
 * `@viq-admin/types` — the single frontend copy of the VendoIQ entity
 * shapes, shared by the customer SPA (`viq-admin/src`) and the super-admin
 * SPA (`apps/console/src`). Mirrors the backend Zod schemas
 * (viq-backend/shared/src/types/*); the server is the source of truth for
 * shape — these are client-side type hints. Publishing `@viq-backend/shared`
 * to eliminate this copy is a separate, deferred infra workstream (ADR 010).
 *
 * `Role` is widened to include `super_admin` so the customer SPA (which
 * references it) keeps compiling unchanged; the super-admin plane has its own
 * role model in `apps/console/src/ability`.
 */

export type Role =
  | 'super_admin'
  | 'company_admin'
  | 'stakeholder'
  | 'field_rep';

// ── CRM connections (Salesforce export, HubSpot/Zoho OAuth — p4) ────────────
export type CrmSource = 'salesforce' | 'hubspot' | 'zoho';

export interface CrmConnectionConfig {
  consumerKey: string;
  loginUrl: string;
  apiVersion: string;
  externalIdField: string;
  /**
   * Owner-fallback-chain settings (Task 7): a company-wide default owner id
   * and a `conferenceId -> ownerId` override map. These live in the same
   * `config` JSON as the Salesforce-specific fields above, but — unlike
   * them — apply to whichever CRM source is actually connected (Salesforce,
   * HubSpot, or Zoho), and are written only through the dedicated
   * `PUT /v1/admin/crm/{source}/owner-defaults` route, never through this
   * connection-credentials shape (`CrmConnectionInput.config` stays the
   * strict 4-field Salesforce shape below). Both optional so a connection
   * with neither configured still parses. See viq-backend
   * `shared/src/types/crm.ts`'s `CrmMergeSettingsSchema`.
   */
  defaultOwnerId?: string;
  defaultOwnerByConference?: Record<string, string>;
}

/**
 * `companyId`/`companyName` are OPTIONAL, not dropped: the super-admin
 * console's cross-tenant Integrations screens (list/edit/detail) display
 * them for a Salesforce connection (GET /v1/superadmin/crm/connections),
 * while the customer portal's own-company plane (GET /v1/admin/crm/
 * connections, p4) omits them entirely — the caller's token already scopes
 * the company. Optional keeps both planes typechecking against one shared
 * shape; console call sites already read them via `?? ''`/`?? companyId`
 * fallbacks, so widening to optional here doesn't break them.
 *
 * `config` is `Partial<CrmConnectionConfig> & Record<string, unknown>`
 * because HubSpot/Zoho connections don't carry the Salesforce-shaped fields
 * (consumerKey/loginUrl/apiVersion/externalIdField) — their config is
 * provider-specific and still being defined (p5/p6). `CrmConnectionInput`
 * (the console's create/edit body) keeps the full, required
 * `CrmConnectionConfig` — it's Salesforce-only today.
 */
export interface CrmConnection {
  id: string;
  companyId?: string;
  companyName?: string;
  crmSource: CrmSource;
  config: Partial<CrmConnectionConfig> & Record<string, unknown>;
  hasSecret: boolean;
  /**
   * How the credential is maintained (`'oauth'` = portal Connect broker,
   * `'credentials'` = operator-entered secrets). Superadmin responses only;
   * optional so the portal's own-company plane — which omits it — and
   * pre-managedBy backends still parse.
   */
  managedBy?: 'oauth' | 'credentials';
  createdAt: string;
  updatedAt: string;
}

export interface CrmConnectionInput {
  /**
   * OPTIONAL (p6): the super-admin console's cross-tenant create/edit
   * always carries a companyId (chosen on create, the record's own
   * immutable value re-attached on edit — the backend's PUT schema
   * requires it structurally). The customer portal's own-company plane
   * (POST/PUT /v1/admin/crm/connections, p6) never sends one — the
   * caller's token already scopes the company, and the backend schema
   * for that plane has no companyId field at all, so a body-supplied
   * one would be stripped even if sent. Optional here keeps one shared
   * input shape typechecking against both planes' actual wire bodies.
   */
  companyId?: string;
  crmSource: CrmSource;
  config: CrmConnectionConfig;
  // Sent only when non-empty; omitting preserves the stored secret.
  secrets?: { consumerSecret: string };
}

export interface Company {
  id: string;
  name: string;
  entraHint: string | null;
  storeCardImages: boolean;
  /**
   * Per-provider entitlements for the customer-plane CRM surface —
   * connecting that provider and the Clean / Match / Send flow built on it.
   * Staff-granted in the console; the backend refuses that provider's routes
   * with CRM_DISABLED while its flag is false. Independent of each other.
   */
  crmHubspotEnabled: boolean;
  crmSalesforceEnabled: boolean;
  crmZohoEnabled: boolean;
  /**
   * Portal self-serve lead moves. Staff-granted in the console; the backend
   * refuses the portal's move endpoints with FEATURE_DISABLED while false.
   * The console's own move is never gated by it.
   */
  leadMoveEnabled: boolean;
  /**
   * Automatic Apollo phone lookup for leads still missing a phone a few
   * minutes after scan. Staff-granted in the console; off by default. Never
   * overwrites a rep-typed or badge-filled phone. The console's manual
   * Enrich is never gated by it.
   */
  autoEnrichApollo: boolean;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface Conference {
  id: string;
  name: string;
  location: string | null;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface ConferenceRef {
  id: string;
  name: string;
}

/**
 * Full user shape used by the super-admin plane (mirrors the backend user
 * record). The customer SPA keeps its own `UserProfile` (a superset with
 * conference-assignment joins) in `src/api/types.ts`.
 */
export interface User {
  oid: string;
  email: string;
  name: string;
  companyId: string;
  role: Role;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  deactivatedAt: string | null;
  deactivatedBy?: string | null;
  deactivationReason?: string | null;
}

export type ToolkitType =
  | 'attendee_list'
  | 'plus_lead'
  | 'expo_tools'
  | 'badge_kit'
  | 'conexsys'
  | 'xpress_lead_pro'
  | 'maritz_swap'
  | 'edc'
  | 'exposmart'
  | 'ats'
  | 'fpleads'
  | 'showcare'
  | 'ats_api';

export interface ToolkitResponse {
  conferenceId: string;
  toolkitType: ToolkitType;
  config: Record<string, unknown>;
  hasSecret: boolean;
  updatedAt: string;
  /** Set when this conference's toolkit was last stamped from a reusable config. */
  sourceConfigId: string | null;
  sourceConfigName: string | null;
}

export interface ToolkitConfig {
  id: string;
  name: string;
  toolkitType: ToolkitType;
  config: Record<string, unknown>;
  hasSecret: boolean;
  appliedConferenceCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ToolkitConfigInput {
  name: string;
  toolkitType: ToolkitType;
  config: Record<string, string>;
  secrets?: Record<string, string>;
}

export type CaptureType = 'voice' | 'badge' | 'card';

export type TagCategory = 'vip' | 'status' | 'custom';

export interface LeadTag {
  category: TagCategory;
  value: string;
}

/** Predefined relationship-status values; canonical for the SPA's UI dropdown. */
export const STATUS_VALUES = [
  'lead',
  'contact',
  'customer',
  'ex_customer',
  'partner',
  'competitor',
  'unknown',
] as const;
export type StatusValue = (typeof STATUS_VALUES)[number];

/** Cross-conference identity match strength surfaced per lead row. */
export type RepeatScanMatch = 'email' | 'name_company' | null;

export interface RepeatScanHistoryEntry {
  event_id: string;
  event_name: string;
  date: string; // ISO yyyy-mm-dd
}

export interface Lead {
  id: string;
  conferenceId: string;
  companyId: string;
  repOid: string;
  /** Human-readable rep name resolved by the backend join; '(Inactive)' suffix when deactivated. */
  repName: string;
  captureType: CaptureType;
  preview: string;
  createdAt: string;
  /** ISO timestamp set by the rep when they delete a lead on their phone; null for active leads. */
  deletedAt: string | null;
  /** Tags attached to this lead (Batch screen). Empty array when none. Added in T4 backend. */
  tags: LeadTag[];
  /** Cross-conference repeat-scan match strength. Added in T6 backend. */
  repeatScanMatch: RepeatScanMatch;
  /** Up to 3 prior events where this lead was matched (email match only). */
  repeatScanHistory: RepeatScanHistoryEntry[];
  /** Contact's employer string (NOT the VendoIQ-internal companyId). */
  companyName: string | null;
  title: string | null;
  /**
   * Contact email/phone, projected by `GET /v1/admin/leads` (spec
   * 2026-08-30-crm-gates-become-defaults §7). The backend coerces blank and
   * whitespace-only to `null` at its mapping layer, so `null` is the ONLY
   * "missing" signal — never test for `''`. A malformed-but-present value
   * (e.g. `bob@`) arrives verbatim and non-null on purpose: the operator
   * needs to see it to fix it.
   *
   * OPTIONAL, and the distinction is load-bearing. These two are new fields
   * on this response and the SPA deploys separately from the backend, so a
   * rollback is possible: `undefined` means "this backend does not project
   * the field", which is absence of INFORMATION and must never be rendered
   * as a finding. `null` means the backend looked and the lead has none —
   * that is the thing worth flagging. Anything reading these for a warning
   * must test `=== null`, not falsiness (see `BatchTable`'s
   * `blankCellStyle`).
   */
  email?: string | null;
  phone?: string | null;
  temperature: string | null;
  notesCount: number;
  openTaskCount: number;
  /** Most recent rawnote.summary or null. */
  aiSummary: string | null;
  /**
   * Free-text "Assigned to" — manager-set, decoupled from CRM.
   * Null when unset; manager-typed string otherwise.
   */
  assignedTo: string | null;
  /**
   * Company lead rules for this lead (spec 2026-09-14 §2.2). `null` = the
   * company's enforcement is off. OPTIONAL for the same deploy-skew reason as
   * `email`: `undefined` means this backend doesn't project it yet, which must
   * never be rendered as a finding.
   */
  completeness?: LeadCompleteness | null;
  /** When the lead was last sent to a CRM; null if never; undefined on an older backend. */
  crmSentAt?: string | null;
  /**
   * The stored split name (`persons.first_name` / `last_name`, verbatim; the
   * columns are NOT NULL, so `''` means "not captured"). `preview` stays the
   * display name. Prefills the Batch grid's First / Last popover (spec
   * 2026-09-17 §1.6). OPTIONAL for the same deploy-skew reason as `email`:
   * `undefined` means an older backend that doesn't project them.
   */
  firstName?: string;
  lastName?: string;
  /**
   * The newest live rep note on this lead (mirrors viq-backend
   * `shared/src/types/lead.ts` `Lead.latestNote`), or null when it has none.
   * Lead-move notes (`capture_method = 'system'`) never count, so a moved
   * lead shows the rep's words; `notesCount` still counts every live note.
   * `serverVersion` is the note's current version, sent back as
   * `expectedVersion` on PATCH …/leads/:id/notes/:noteId. The live feed
   * carries null. OPTIONAL for the same deploy-skew reason as `email`:
   * `undefined` means an older backend that doesn't project it.
   */
  latestNote?: LeadLatestNote | null;
}

/** `Lead.latestNote`: one `rawnotes` row, as the leads list projects it. */
export interface LeadLatestNote {
  id: string;
  text: string;
  /** rawnotes.server_version at read time. */
  serverVersion: number;
  /** ISO 8601, same format as `Lead.createdAt`. */
  createdAt: string;
}

export interface LeadsPage {
  leads: Lead[];
  nextCursor: string | null;
}

/**
 * `lead` in the 200 body of PATCH /v1/{admin,superadmin}/leads/:id/fields
 * (mirrors viq-backend `shared/src/types/lead.ts` `LeadFieldsUpdatedLead`,
 * spec 2026-09-17 §1.4). Field names and blank handling match the list row:
 * `preview` is the (server-derived) display name, `companyName` the contact's
 * employer, a blank email/phone `null`. `completeness` / `crmSentAt` are null
 * when the company has lead rules off AND when computing them failed after
 * the write committed — the edit still landed; the next list load carries
 * the real values.
 */
export interface LeadFieldsUpdatedLead {
  id: string;
  preview: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  temperature: string | null;
  status: string;
  serverVersion: number;
  updatedAt: string;
  completeness: LeadCompleteness | null;
  crmSentAt: string | null;
}

export interface LeadFieldsUpdateResponse {
  lead: LeadFieldsUpdatedLead;
}

// ── AI prompt overrides (Epic viq-backend#41) ────────────────────────

// Finding I-7: photo/voice_text are the original capture pipelines; the
// three enrichment_* values are the contact-enrichment pipelines (viq-vault
// spec 2026-09-23-contact-enrichment-agent, plan 2) — mirrors the backend's
// PromptPipelineEnum (viq-backend shared/src/types/extraction.ts).
export type PromptPipeline =
  | 'photo'
  | 'voice_text'
  | 'enrichment_research'
  | 'enrichment_conference'
  | 'enrichment_capture';

/** GET /v1/superadmin/core-prompts — one summary per pipeline. */
export interface CorePromptSummary {
  pipeline: PromptPipeline;
  activeVersion: number | null;
  activeVersionId: string | null;
  note: string | null;
  versionCount: number;
}

export interface CoreVersion {
  id: string;
  pipeline: PromptPipeline;
  version: number;
  body: string;
  note: string | null;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
}

/** Row of the Prompt Rollout company grid. */
export interface CompanyPromptGridRow {
  companyId: string;
  name: string;
  pinnedVersion: number;
  isBehindActive: boolean;
  hasAppend: boolean;
  sourceTemplateName: string | null;
  updatedAt: string;
}

/** Per-company, per-pipeline prompt state (Company detail). */
export interface CompanyPromptRow extends CompanyPromptGridRow {
  pipeline: PromptPipeline;
  append: string | null;
  sourceTemplateId: string | null;
}

/** Reusable append template (dataProvider resource `prompt-templates`). */
export interface PromptTemplate {
  id: string;
  name: string;
  pipeline: PromptPipeline;
  body: string;
  appliedCompanyCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PromptTemplateInput {
  name: string;
  pipeline: PromptPipeline;
  body: string;
}

// ── Company lead rules (viq-vault spec 2026-09-14-company-lead-rules-design) ──
// Mirrors viq-backend shared/src/leadRules/{schema,evaluate,wire}.ts and
// db/{coverage,aggregation}.ts. The server is the source of truth; the literals
// in packages/types/__contract__/leadRules.contract.ts pin these shapes.

export type LeadEnforcement = 'off' | 'flag' | 'block';

export const LEAD_FIELDS = [
  'first_name',
  'last_name',
  'company',
  'title',
  'email',
  'phone',
  'linkedin_url',
  'status',
  'temperature',
] as const;
export type LeadField = (typeof LEAD_FIELDS)[number];

/** Backend save limits — the editor mirrors them so a save is never refused for a knowable reason. */
export const NOTES_MIN_CHARS_MAX = 5000;
export const LEAD_REQUIREMENTS_MAX = 20;

export type LeadRequirement =
  | { id: string; type: 'notes_min_chars'; min: number }
  /** `questionId` is an INVENTORY question id (discovery_questions.id). */
  | { id: string; type: 'question_answered'; questionId: string }
  /** `hold` (spec 2026-09-28 §6): staff-only; holds the lead back under 'block' until the field is filled. */
  | { id: string; type: 'field_present'; field: LeadField; hold?: boolean };

/** Coverage line for a question requirement: "Applies at 6 of 7 conferences". */
export interface LeadRuleCoverage {
  /** Current inventory wording; null when the question was deleted from the inventory. */
  questionText: string | null;
  questionDeleted: boolean;
  askedAtCount: number;
  conferenceCount: number;
  notAskedAt: Array<{ id: string; name: string }>;
}

/** GET/PUT /v1/admin/company/lead-rules and /v1/superadmin/companies/:id/lead-rules. */
export interface LeadRulesResponse {
  enforcement: LeadEnforcement;
  requirements: LeadRequirement[];
  /** 0 when the company has never saved rules. Echo it back as `expectedVersion`. */
  version: number;
  updatedAt: string | null;
  updatedBy: { oid: string; name: string | null } | null;
  /** Keyed by requirement id; question requirements only. */
  coverage: Record<string, LeadRuleCoverage>;
}

export interface SaveLeadRulesInput {
  enforcement: LeadEnforcement;
  requirements: LeadRequirement[];
  expectedVersion: number;
}

export type LeadRuleFailure =
  | { requirementId: string; type: 'notes_min_chars'; have: number; need: number }
  | { requirementId: string; type: 'question_answered'; questionId: string; questionText: string }
  | { requirementId: string; type: 'field_present'; field: LeadField };

export interface LeadCompleteness {
  /** Every applicable requirement is met. */
  complete: boolean;
  /** Enforcement is 'block' and the notes minimum failed or a held field is missing (spec 2026-09-28 §6). */
  sendBlocked: boolean;
  /**
   * Progress toward the notes minimum, met or not — drives the "312 / 500"
   * counter on the lead detail. null when the checklist has no notes minimum.
   */
  notes: { have: number; need: number } | null;
  /** In requirement order. */
  failures: LeadRuleFailure[];
  /** Under 'block': the missing fields whose requirement holds the lead. Absent when none. */
  held?: LeadField[];
}

/** `leadRules` on the Batch aggregations response — drives the Total Notes tile. */
export interface LeadRulesAggregation {
  enforcement: LeadEnforcement;
  notesMin: number | null;
  notesShortCount: number | null;
}
